'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { ArrowRight, Mic, Play, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface LandingHeroProps {
  isAuthenticated?: boolean;
  salesDemoHref?: string | null;
}

export function LandingHero({ salesDemoHref }: LandingHeroProps) {
  const t = useTranslations('landing.hero');
  const [showVideo, setShowVideo] = useState(false);

  return (
    <section
      data-theme="dark"
      className="relative overflow-hidden pt-[52px] pb-[80px] lg:pt-[64px] min-h-screen flex items-center"
    >
      {/* Aurora wave gradient */}
      <div className="aurora-field absolute inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Base dark overlay for smooth blending */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black" />

        {/* Blob 1 — magenta/pink, left-center */}
        <div
          className="absolute w-[900px] h-[600px] rounded-full opacity-70"
          style={{
            background: 'radial-gradient(circle, #FF00CC 0%, transparent 70%)',
            filter: 'blur(120px)',
            top: '-10%',
            left: '10%',
            animation: 'auroraBlob1 12s ease-in-out infinite',
          }}
        />
        {/* Blob 2 — deep purple/violet, center */}
        <div
          className="absolute w-[800px] h-[700px] rounded-full opacity-60"
          style={{
            background: 'radial-gradient(circle, #7B2FFF 0%, transparent 70%)',
            filter: 'blur(100px)',
            top: '-5%',
            left: '30%',
            animation: 'auroraBlob2 10s ease-in-out infinite',
          }}
        />
        {/* Blob 3 — electric blue, right */}
        <div
          className="absolute w-[700px] h-[500px] rounded-full opacity-50"
          style={{
            background: 'radial-gradient(circle, #0066FF 0%, transparent 70%)',
            filter: 'blur(110px)',
            top: '5%',
            right: '5%',
            animation: 'auroraBlob3 14s ease-in-out infinite',
          }}
        />
        {/* Blob 4 — hot pink/coral, far right */}
        <div
          className="absolute w-[600px] h-[500px] rounded-full opacity-55"
          style={{
            background: 'radial-gradient(circle, #FF3366 0%, transparent 70%)',
            filter: 'blur(100px)',
            top: '-15%',
            right: '-5%',
            animation: 'auroraBlob1 11s ease-in-out infinite reverse',
          }}
        />
        {/* Blob 5 — cyan accent, subtle bottom-center glow */}
        <div
          className="absolute w-[500px] h-[400px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, #00D4FF 0%, transparent 70%)',
            filter: 'blur(90px)',
            top: '20%',
            left: '45%',
            animation: 'auroraBlob3 9s ease-in-out infinite',
          }}
        />
      </div>

      <style jsx>{`
        @keyframes auroraBlob1 {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(40px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-30px, 20px) scale(0.95);
          }
        }
        @keyframes auroraBlob2 {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(-50px, 25px) scale(1.05);
          }
          66% {
            transform: translate(30px, -40px) scale(1.1);
          }
        }
        @keyframes auroraBlob3 {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, 35px) scale(1.08);
          }
          66% {
            transform: translate(-40px, -20px) scale(0.95);
          }
        }
        @keyframes waveBar {
          0% {
            transform: scaleY(0.3);
          }
          100% {
            transform: scaleY(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .aurora-field > div {
            animation: none;
          }
          .animate-float {
            animation: none;
          }
        }
      `}</style>

      <div className="container max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-10">
          {/* Left Column: Text & CTAs */}
          <div className="lg:w-[40%] flex flex-col items-start text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80 backdrop-blur-sm mb-6"
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              {t('aiInterviewPlatform')}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-[52px] font-serif font-medium tracking-tight text-white leading-[1.1]"
            >
              {t('headline1')}
              <br />
              {t('headline2')}
              <br />
              <span className="text-white/45 italic">{t('headline3')}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
              className="mt-6 max-w-md text-base leading-relaxed text-white/75"
            >
              {t('description')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
              className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <Link
                href="/login?mode=signup"
                className="group inline-flex h-12 items-center justify-center rounded-full bg-primary px-7 font-medium text-white transition-all duration-300 hover:brightness-110 hover:shadow-[0_0_28px_rgba(255,108,92,0.45)]"
              >
                <span className="flex items-center gap-2 text-[15px]">
                  {t('getStarted')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>

              {salesDemoHref && (
                <Link
                  href={salesDemoHref}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 text-[15px] font-medium text-white transition-all duration-300 hover:bg-white/10"
                >
                  {t('bookADemo')}
                </Link>
              )}
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-4 text-xs text-white/50 font-medium tracking-wide"
            >
              {t('freeTrial')}
            </motion.p>
          </div>

          {/* Right Column: Room preview / Launch video */}
          <section
            aria-label="Interview room preview"
            className="lg:w-[60%] relative w-full hidden md:flex flex-col gap-4"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-white/60">
                {showVideo ? t('watchLaunchVideoAlt') : t('previewText')}
              </p>
              <button
                type="button"
                onClick={() => setShowVideo((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 transition-all duration-300 hover:bg-white/10 hover:text-white"
              >
                {showVideo ? (
                  <>
                    <X className="h-4 w-4" />
                    {t('backToPreview')}
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    {t('watchLaunchVideo')}
                  </>
                )}
              </button>
            </div>

            <AnimatePresence mode="wait">
              {showVideo ? (
                <motion.div
                  key="video"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="relative w-full"
                >
                  <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_40px_100px_-20px_rgba(0,0,0,0.4)]">
                    <video
                      className="w-full h-auto"
                      src="/videos/bringtable-launch.mp4"
                      controls
                      autoPlay
                      playsInline
                      preload="auto"
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="preview"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.35, ease: 'easeOut' }}
                  className="relative w-full"
                  style={{ perspective: '1200px' }}
                >
                  {/* Main Dashboard Card with 3D tilt */}
                  <div
                    className="relative w-full rounded-2xl border border-black/10 bg-white shadow-[0_40px_100px_-20px_rgba(0,0,0,0.3)] overflow-hidden"
                    style={{
                      transform: 'rotateY(-8deg) rotateX(4deg)',
                      transformStyle: 'preserve-3d',
                    }}
                  >
                    {/* Browser chrome */}
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-black/5 bg-[#f8f8f8]">
                      <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                        <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                        <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                      </div>
                      <div className="mx-auto px-4 py-1 rounded-md bg-black/[0.04] border border-black/5 text-[10px] text-black/40 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        app.bringtable.com/screening
                      </div>
                      <div className="flex gap-1.5">
                        <div className="w-4 h-4 rounded bg-black/[0.03] border border-black/5" />
                        <div className="w-4 h-4 rounded bg-black/[0.03] border border-black/5" />
                      </div>
                    </div>

                    {/* App layout: sidebar + main */}
                    <div className="flex min-h-[340px]">
                      {/* Sidebar */}
                      <div className="w-[48px] border-r border-black/5 bg-[#fafafa] flex flex-col items-center pt-3 gap-3">
                        <div className="w-7 h-7 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center">
                          <div className="w-3 h-3 rounded bg-primary/60" />
                        </div>
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className="w-7 h-7 rounded-lg bg-black/[0.03] border border-black/5 flex items-center justify-center"
                          >
                            <div className="w-3 h-2 rounded-sm bg-black/10" />
                          </div>
                        ))}
                        <div className="mt-auto mb-3 w-6 h-6 rounded-full bg-black/[0.04] border border-black/5" />
                      </div>

                      {/* Main content */}
                      <div className="flex-1 flex flex-col">
                        {/* Top toolbar */}
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-black/5">
                          <div className="flex items-center gap-3">
                            <p className="text-black text-xs font-semibold">Screening Queue</p>
                            <div className="px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-[9px] text-primary font-medium">
                              12 active
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/[0.03] border border-black/5">
                              <svg
                                className="w-3 h-3 text-black/30"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <circle cx="11" cy="11" r="8" />
                                <path d="M21 21l-4.35-4.35" />
                              </svg>
                              <span className="text-[10px] text-black/30">Search candidates…</span>
                            </div>
                            <div className="px-2 py-1 rounded-md bg-black/[0.03] border border-black/5 text-[10px] text-black/30">
                              Filters
                            </div>
                          </div>
                        </div>

                        {/* Column headers */}
                        <div className="flex items-center px-4 py-1.5 border-b border-black/[0.04] text-[9px] text-black/35 font-medium uppercase tracking-wider">
                          <div className="w-[28%]">Candidate</div>
                          <div className="w-[18%]">Role</div>
                          <div className="w-[14%]">Score</div>
                          <div className="w-[20%]">Status</div>
                          <div className="w-[20%] text-right">Date</div>
                        </div>

                        {/* Data rows */}
                        {[
                          {
                            name: 'Sarah Kim',
                            initials: 'SK',
                            email: 'sarah.k@gmail.com',
                            role: 'Sr. Engineer',
                            score: 91,
                            status: 'Completed',
                            statusColor: 'text-emerald-600',
                            statusBg: 'bg-emerald-50 border-emerald-200',
                            date: 'Today, 2:30 PM',
                            dot: 'bg-emerald-500',
                          },
                          {
                            name: 'Marcus Torres',
                            initials: 'MT',
                            email: 'marcus.t@outlook.com',
                            role: 'Sr. Engineer',
                            score: 84,
                            status: 'Completed',
                            statusColor: 'text-emerald-600',
                            statusBg: 'bg-emerald-50 border-emerald-200',
                            date: 'Today, 1:15 PM',
                            dot: 'bg-emerald-500',
                          },
                          {
                            name: 'Priya Mehta',
                            initials: 'PM',
                            email: 'priya.m@company.io',
                            role: 'Staff Engineer',
                            score: null,
                            status: 'In Progress',
                            statusColor: 'text-amber-600',
                            statusBg: 'bg-amber-50 border-amber-200',
                            date: 'Now',
                            dot: 'bg-amber-500 animate-pulse',
                          },
                          {
                            name: 'James Liu',
                            initials: 'JL',
                            email: 'james.l@techco.dev',
                            role: 'Sr. Engineer',
                            score: null,
                            status: 'Scheduled',
                            statusColor: 'text-black/35',
                            statusBg: 'bg-black/[0.03] border-black/10',
                            date: 'Tomorrow',
                            dot: 'bg-black/20',
                          },
                          {
                            name: 'Elena Vasquez',
                            initials: 'EV',
                            email: 'elena.v@startup.co',
                            role: 'Lead Engineer',
                            score: 78,
                            status: 'Completed',
                            statusColor: 'text-emerald-600',
                            statusBg: 'bg-emerald-50 border-emerald-200',
                            date: 'Yesterday',
                            dot: 'bg-emerald-500',
                          },
                        ].map((c, i) => (
                          <div
                            key={i}
                            className={`flex items-center px-4 py-2.5 border-b border-black/[0.04] hover:bg-black/[0.02] transition-colors ${i === 2 ? 'bg-primary/[0.02]' : ''}`}
                          >
                            <div className="w-[28%] flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-black/[0.04] border border-black/5 flex items-center justify-center text-[9px] text-black/50 font-medium shrink-0">
                                {c.initials}
                              </div>
                              <div>
                                <p className="text-black text-[11px] font-medium leading-tight">
                                  {c.name}
                                </p>
                                <p className="text-black/35 text-[9px] leading-tight">{c.email}</p>
                              </div>
                            </div>
                            <div className="w-[18%] text-[10px] text-black/45">{c.role}</div>
                            <div className="w-[14%]">
                              {c.score ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-10 h-1 bg-black/5 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-emerald-500 rounded-full"
                                      style={{ width: `${c.score}%` }}
                                    />
                                  </div>
                                  <span className="text-[10px] text-emerald-600 font-mono">
                                    {c.score}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[10px] text-black/20">—</span>
                              )}
                            </div>
                            <div className="w-[20%]">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-medium ${c.statusBg} ${c.statusColor}`}
                              >
                                <span className={`w-1 h-1 rounded-full ${c.dot}`} />
                                {c.status}
                              </span>
                            </div>
                            <div className="w-[20%] text-right text-[10px] text-black/30">
                              {c.date}
                            </div>
                          </div>
                        ))}

                        {/* Bottom bar */}
                        <div className="flex items-center justify-between px-4 py-2 mt-auto border-t border-black/5">
                          <span className="text-[9px] text-black/25">
                            Showing 5 of 12 candidates
                          </span>
                          <div className="flex gap-1">
                            <div className="px-2 py-0.5 rounded bg-black/[0.03] text-[9px] text-black/30">
                              ← Prev
                            </div>
                            <div className="px-2 py-0.5 rounded bg-primary/15 text-[9px] text-primary font-medium">
                              1
                            </div>
                            <div className="px-2 py-0.5 rounded bg-black/[0.03] text-[9px] text-black/30">
                              2
                            </div>
                            <div className="px-2 py-0.5 rounded bg-black/[0.03] text-[9px] text-black/30">
                              Next →
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Live Interview Room */}
                  <div
                    className="absolute -right-4 top-4 rounded-2xl border border-black/10 bg-white/95 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.15)] animate-float-delayed w-[240px] overflow-hidden"
                    style={{
                      transform: 'rotateY(-8deg) rotateX(4deg)',
                      transformStyle: 'preserve-3d',
                    }}
                  >
                    {/* Room header */}
                    <div className="flex items-center justify-between px-3 py-2 border-b border-black/5 bg-[#fafafa]">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-[9px] text-red-500 font-mono font-semibold">
                          LIVE
                        </span>
                      </div>
                      <span className="text-[9px] text-black/30 font-mono">00:12:34</span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-black/[0.04] border border-black/5 flex items-center justify-center">
                          <Mic className="w-2 h-2 text-black/30" />
                        </div>
                        <div className="w-4 h-4 rounded bg-red-50 border border-red-200 flex items-center justify-center">
                          <div className="w-2 h-1.5 rounded-sm bg-red-400" />
                        </div>
                      </div>
                    </div>

                    {/* Interview visual area */}
                    <div className="px-3 py-3 flex items-center gap-3 bg-gradient-to-br from-[#f8f7ff] to-white">
                      {/* AI avatar */}
                      <div className="relative shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-purple-400/15 border border-black/8 flex items-center justify-center">
                          <div className="w-7 h-7 rounded-full bg-white border border-black/10 flex items-center justify-center shadow-sm">
                            <svg
                              className="w-3.5 h-3.5 text-primary"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                              <line x1="12" y1="19" x2="12" y2="23" />
                            </svg>
                          </div>
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
                      </div>
                      {/* Waveform + status */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] text-black/70 font-medium">
                            Bringtable AI
                          </span>
                          <span className="text-[8px] text-primary font-medium bg-primary/8 px-1.5 py-0.5 rounded-full">
                            Speaking
                          </span>
                        </div>
                        <div className="flex items-end gap-[2px] h-4">
                          {[3, 6, 9, 5, 8, 4, 7, 10, 6, 8, 4, 7, 5, 9, 3, 6].map((h, i) => (
                            <div
                              key={i}
                              className="w-[2.5px] bg-primary/40 rounded-full"
                              style={{
                                height: `${h * 10}%`,
                                animation: `waveBar 1s ease-in-out ${i * 0.06}s infinite alternate`,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Live transcript */}
                    <div className="px-3 py-2 border-t border-black/5 flex flex-col gap-1.5">
                      <div className="flex items-start gap-2">
                        <div className="w-3.5 h-3.5 rounded-full bg-primary/15 shrink-0 mt-0.5 flex items-center justify-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                        </div>
                        <p className="text-[9px] text-black/60 leading-relaxed">
                          Can you walk me through how you&apos;d design a real-time notification
                          system?
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-3.5 h-3.5 rounded-full bg-blue-400/15 shrink-0 mt-0.5 flex items-center justify-center">
                          <span className="text-[6px] font-bold text-blue-500">PM</span>
                        </div>
                        <p className="text-[9px] text-black/50 leading-relaxed">
                          Sure — I&apos;d start with event sourcing using Kafka, then fan out via
                          WebSockets…
                        </p>
                      </div>
                    </div>

                    {/* Real-time scoring */}
                    <div className="px-3 py-2 border-t border-black/5 bg-[#fafafa] flex items-center gap-2">
                      <div className="flex gap-1.5 flex-1">
                        {[
                          { label: 'Depth', value: 88, color: 'bg-emerald-400' },
                          { label: 'Clarity', value: 92, color: 'bg-blue-400' },
                          { label: 'Structure', value: 75, color: 'bg-amber-400' },
                        ].map((m) => (
                          <div key={m.label} className="flex-1">
                            <div className="flex justify-between mb-0.5">
                              <span className="text-[6px] text-black/30">{m.label}</span>
                              <span className="text-[6px] text-black/40 font-mono">{m.value}</span>
                            </div>
                            <div className="h-1 bg-black/5 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${m.color} rounded-full`}
                                style={{ width: `${m.value}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Floating Candidate Scorecard */}
                  <div
                    className="absolute -left-4 bottom-6 rounded-2xl border border-black/10 bg-white/95 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.15)] animate-float w-[250px] overflow-hidden"
                    style={{
                      transform: 'rotateY(-8deg) rotateX(4deg)',
                      transformStyle: 'preserve-3d',
                    }}
                  >
                    {/* Card header */}
                    <div className="flex items-center gap-3 p-3 border-b border-black/5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400/50 to-purple-400/50 border border-black/10 flex items-center justify-center">
                        <span className="text-[9px] text-white font-bold">SK</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[11px] text-black/80 font-semibold block leading-tight">
                          Sarah Kim
                        </span>
                        <span className="text-[9px] text-black/35">Sr. Frontend Engineer</span>
                      </div>
                      <div className="px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-[8px] text-emerald-600 font-medium">Advance</span>
                      </div>
                    </div>

                    {/* Score + rubric */}
                    <div className="p-3 flex gap-3">
                      {/* Score ring */}
                      <div className="w-14 h-14 shrink-0 relative flex items-center justify-center">
                        <svg
                          className="absolute inset-0 w-full h-full -rotate-90"
                          viewBox="0 0 36 36"
                        >
                          <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="none"
                            stroke="rgba(0,0,0,0.04)"
                            strokeWidth="2.5"
                          />
                          <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="none"
                            stroke="rgba(52,211,153,0.7)"
                            strokeWidth="2.5"
                            strokeDasharray="94.2"
                            strokeDashoffset="8.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="text-sm font-mono text-emerald-600 font-bold relative z-10">
                          91
                        </span>
                      </div>
                      {/* Breakdown */}
                      <div className="flex-1 flex flex-col gap-1.5 justify-center">
                        {[
                          { label: 'Technical', score: 94, color: 'bg-emerald-400' },
                          { label: 'Communication', score: 89, color: 'bg-blue-400' },
                          { label: 'Problem Solving', score: 91, color: 'bg-emerald-400' },
                          { label: 'Culture Fit', score: 85, color: 'bg-primary' },
                        ].map((r) => (
                          <div key={r.label} className="flex items-center gap-2">
                            <span className="text-[7px] text-black/35 w-16 truncate">
                              {r.label}
                            </span>
                            <div className="flex-1 h-1.5 bg-black/5 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${r.color} rounded-full`}
                                style={{ width: `${r.score}%` }}
                              />
                            </div>
                            <span className="text-[7px] text-black/30 font-mono w-4 text-right">
                              {r.score}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* AI summary */}
                    <div className="px-3 pb-3">
                      <div className="bg-[#f8f8f8] border border-black/5 rounded-lg p-2.5">
                        <p className="text-[8px] text-black/35 uppercase tracking-wider font-medium mb-1">
                          AI Summary
                        </p>
                        <p className="text-[9px] text-black/60 leading-relaxed">
                          &quot;Exceptional depth in distributed systems. Clear, structured
                          communicator. Top 5% of candidates.&quot;
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        </div>
      </div>
    </section>
  );
}
